import React, { FC } from "react";
import { useTranslation } from "react-i18next";

import { formatLocation } from "@/data/locations";
import { IWorkOrder } from "@/data/work-orders";
import { Col, Row } from "@/eworkitComponents";
import { useFormats } from "@/hooks/Formats";
import { JobRequestDetailIconSection } from "@/views/JobRequests/Detail/components";

interface IWorkOrderDigestProps {
  workOrder: IWorkOrder;
}

export const WorkOrderDigest: FC<IWorkOrderDigestProps> = ({ workOrder }) => {
  const { t } = useTranslation("session");
  const { formatDate, formatRate, formatCost } = useFormats();

  return (
    <Row>
      {!!workOrder.hourlyRate && !!workOrder.rateCurrency && (
        <Col width={{ xxs: 12, xs: 6, sm: 4, lg: 3 }}>
          <JobRequestDetailIconSection
            label={t("WORK_ORDERS.DETAILS.RATE")}
            value={formatRate(workOrder.hourlyRate, workOrder.rateCurrency)}
            icon="cash_multiple"
            grow={0}
          />
        </Col>
      )}

      <Col width={{ xxs: 12, xs: 6, sm: 4, lg: 3 }}>
        <JobRequestDetailIconSection
          label={t("WORK_ORDERS.DETAILS.WORKLOAD")}
          value={`${workOrder.hoursPerWeek}${t("WORK_ORDERS.DETAILS.HRS_PER_WEEK")}`}
          icon="financial"
          grow={0}
        />
      </Col>

      <Col width={{ xxs: 12, xs: 6, sm: 4, lg: 3 }}>
        <JobRequestDetailIconSection label={t("WORK_ORDERS.DETAILS.ROLE")} value={workOrder.role} icon="user_fill" />
      </Col>

      <Col width={{ xxs: 12, xs: 6, sm: 4, lg: 3 }}>
        <JobRequestDetailIconSection
          label={t("WORK_ORDERS.DETAILS.SENIORITY")}
          value={workOrder.level}
          icon="business-dev"
        />
      </Col>

      {!!workOrder.location && (
        <Col width={{ xxs: 12, xs: 6, sm: 4, lg: 3 }}>
          <JobRequestDetailIconSection
            label={t("session:JOB_REQUESTS.DETAIL.LOCATION", { count: 1 })}
            value={formatLocation(workOrder.location)}
            icon="pin"
          />
        </Col>
      )}

      <Col width={{ xxs: 12, xs: 6, sm: 4, lg: 3 }}>
        <JobRequestDetailIconSection
          label={t("WORK_ORDERS.DETAILS.SUBMITTED")}
          value={formatDate(workOrder.createdDate)}
          icon="date"
        />
      </Col>

      {workOrder.costCenter && (
        <Col width={{ xxs: 12, xs: 6, sm: 4, lg: 3 }}>
          <JobRequestDetailIconSection
            label={t("WORK_ORDERS.DETAILS.COST_CENTER")}
            value={`${workOrder.costCenter.number}, ${workOrder.costCenter.name}`}
            icon="target_cash"
          />
        </Col>
      )}

      <Col width={{ xxs: 12, xs: 6, sm: 4, lg: 3 }}>
        <JobRequestDetailIconSection
          label={t("JOB_REQUESTS.DETAIL.REMOTENESS.REMOTE")}
          value={`${workOrder.remoteWorkPercentage || 0}%`}
          icon="website"
        />
      </Col>

      {!!workOrder.businessUnitName && (
        <Col width={{ xxs: 12, xs: 6, sm: 4, lg: 3 }}>
          <JobRequestDetailIconSection label="Business Unit" value={workOrder.businessUnitName} icon="company" />
        </Col>
      )}

      {!!workOrder.poNumber && (
        <Col width={{ xxs: 12, xs: 6, sm: 4, lg: 3 }}>
          <JobRequestDetailIconSection label="PO Number" value={workOrder.poNumber} icon="skills" />
        </Col>
      )}

      {!!workOrder.legalEntityName && (
        <Col width={{ xxs: 12, xs: 6, sm: 4, lg: 3 }}>
          <JobRequestDetailIconSection
            label={t("WORK_ORDERS.DETAILS.LEGAL_ENTITY")}
            value={workOrder.legalEntityName}
            icon="company"
          />
        </Col>
      )}

      {!!workOrder.legalEntityVatNo && (
        <Col width={{ xxs: 12, xs: 6, sm: 4, lg: 3 }}>
          <JobRequestDetailIconSection
            label={t("WORK_ORDERS.DETAILS.VAT_NUMBER")}
            value={workOrder.legalEntityVatNo}
            icon="vat"
          />
        </Col>
      )}

      {!!workOrder.estimatedCost && (
        <Col width={{ xxs: 12, xs: 6, sm: 4, lg: 3 }}>
          <JobRequestDetailIconSection
            label={t("WORK_ORDERS.DETAILS.BUDGET")}
            value={formatCost(workOrder.estimatedCost, workOrder.rateCurrency)}
            icon="cash_multiple"
          />
        </Col>
      )}
    </Row>
  );
};
